import { useState, useEffect } from 'react'
import {
  Paper,
  makeStyles,
  Container,
  Link as MuiLink,
  Grid,
  IconButton,
  Collapse,
  useTheme,
} from '@material-ui/core'
import ButtonSp from '../../ui/ButtonSp'
import CloseIcon from '@material-ui/icons/Close'
import clsx from 'clsx'
import delay from '../../utility/delay'
import Link from '../../utility/CustomLink'
import { useTranslation } from 'next-i18next'

const useStyles = makeStyles((theme) => ({
  paper: {
    width: 'clamp(320px, 90%, 680px)',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    },
    boxShadow:
      '3px 5px 12px -1px rgb(0 0 0 / 50%), 0px 1px 1px 0px rgb(0 0 0 / 0%), 0px 1px 3px 0px rgb(0 0 0 / 0%)',
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    position: 'relative',
    maxHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      maxHeight: '90vh',
    },
    transition: '0.5s',
  },
  settingOpen: {
    width: '100%',
  },
  icon: {
    width: 80,
    height: 80,
    [theme.breakpoints.down('sm')]: {
      width: 70,
      height: 70,
      alignSelf: 'flex-start',
      marginTop: theme.spacing(1),
    },
    [theme.breakpoints.down('xs')]: {
      width: 50,
      height: 50,
    },
  },
  flexRowCenter: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-evenly',
  },
  flexColLeft: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    justifyContent: 'center',
  },
  flexColCenter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  button: {
    width: '90%',
    height: 50,
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      margin: 0,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      width: '100%',
    },
  },
  p: {
    margin: 0,
    fontSize : 14,
    padding: `${theme.spacing(1)}px ${theme.spacing(1)}px`,
    color: '#404041',
  },
  title: {
    color: '#4F4F4F',
    fontWeight: 500,
    fontSize: 20,
    ...theme.headerFont,
    margin: `${theme.spacing(1)}px 0`,
    marginLeft: theme.spacing(1),
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(6),
    right: theme.spacing(6),
    [theme.breakpoints.down('sm')]: {
      top: theme.spacing(2),
      right: theme.spacing(2),
    },
  },
  borderBottom: {
    borderBottom: '1px solid #E0E0E0',
  },
  pMarginBottom: {
    marginBottom: theme.spacing(2),
  },
  buttonMargin: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  flex1: {
    flex: 1,
  },
  mobileMaxHeight100vh: {
    [theme.breakpoints.down('sm')]: {
      height: '100vh',
      maxHeight: '100vh',
      width: '100%',
    },
  },
  width100: {
    width: '100%',
  },
  containerFlexBox: {
    display: 'flex',
    justifyContent: 'center',
  },
  paddingTop : {
      paddingTop : 35,
  }
}))

const PdpaNewBanner = ({
  isMobile,
  isTablet,
  settingComponent,
  handleSaveSetting,
  handleAcceptAll,
  editCookiesMode = false,
  handleEditCookiesClose,
  isSettingOpen,
  setSettingOpen,
}) => {
  const theme = useTheme()
  const classes = useStyles()
  const isDesktop = !isMobile && !isTablet
  // const [isSettingOpen,setSettingOpen] = useState(editCookiesMode) // if in editCookiesMode -> true
  const [isFullHeight, setFullHeight] = useState(false)

  const { t } = useTranslation('pdpa')

  const delayedSetFullHeight = async () => {
    await delay(theme.transitions.duration.standard)
    setFullHeight(true)
  }

  const openSetting = () => {
    setSettingOpen(true)
    delayedSetFullHeight()
  }
  const closeSetting = () => {
    document.body.style.overflow = 'unset'
    if (editCookiesMode) {
      handleEditCookiesClose()
    } else {
      setSettingOpen(false)
      setFullHeight(false)
    }
  }

  const _handleSaveSetting = () => {
    document.body.style.overflow = 'unset'
    handleSaveSetting()
  }

  useEffect(() => {
    if (process.browser) {
      if (isSettingOpen) {
        document.body.style.overflow = 'hidden' // lock scrolling of body when setting is open
      } else {
        document.body.style.overflow = 'unset'
      }
    }
  }, [isSettingOpen])

  const renderPdpaTextOnSetting = () => {
    if (isSettingOpen) {
      return (
        <>
          <Grid
            item
            xs={2}
            md={isSettingOpen ? 2 : 1}
            className={classes.flexRowCenter}
          >
            <img
              src="/static/img/icons/privacy_icon.svg"
              alt="pdpa icon"
              className={classes.icon}
            />
          </Grid>
          <Grid
            item
            xs={10}
            md={isSettingOpen ? 8 : 9}
            className={clsx(
              classes.flexColLeft,
              isDesktop && isSettingOpen && classes.borderBottom
            )}
          >
            <h5 className={classes.title}>{t('your-privacy')}</h5>
            <p
              className={clsx(
                classes.p,
                isSettingOpen && classes.pMarginBottom
              )}
            >
              {t('desc-1')}
              <Link href="/privacy">
                <MuiLink component="span"> {t('privacy-policy')} </MuiLink>
              </Link>
            </p>
          </Grid>
        </>
      )
    } else {
      return (
        <Grid
          item
          xs={10}
          className={clsx(
            classes.flexColCenter,
            isDesktop && isSettingOpen && classes.borderBottom
          )}
        >
          <p
            className={clsx(classes.p, isSettingOpen && classes.pMarginBottom)}
          >
            {t('desc-3')}
            <Link href="/privacy">
              <MuiLink component="span"> {t('privacy-policy')} </MuiLink>
            </Link>
            {t('desc-4')}
            <MuiLink onClick={openSetting}> {t('setting')} </MuiLink>
          </p>
        </Grid>
      )
    }
  }

  return (
    <Container className={classes.containerFlexBox} disableGutters={!isDesktop}>
      <Paper
        elevation={0}
        className={clsx(
          classes.paper,
          !isDesktop && isFullHeight && classes.mobileMaxHeight100vh,
          isSettingOpen && classes.settingOpen
        )}
      >
        <Grid
          container
          direction="row"
          className={clsx(!isDesktop && isSettingOpen && classes.borderBottom),isSettingOpen && classes.paddingTop}
        >
          {renderPdpaTextOnSetting()}

          <Grid item xs={2} className={classes.flexRowCenter}>
            {!isSettingOpen && (
              <ButtonSp className={classes.button} onClick={handleAcceptAll}>
                {t('accept-all-2')}
              </ButtonSp>
            )}
          </Grid>
        </Grid>
        <Collapse in={isSettingOpen} style={{ overflow: 'scroll' }}>
          <>
            {/* settings row */}
            <Grid container>
              {isDesktop && <Grid item xs={2}></Grid>}
              <Grid item xs={12} md={8}>
                {settingComponent}
              </Grid>
              {isDesktop && <Grid item xs={2}></Grid>}
            </Grid>
          </>
        </Collapse>

        <div style={{ flex: 1 }} />
        {isSettingOpen && (
          <Grid container>
            {isDesktop && <Grid item xs={2}></Grid>}
            <Grid item xs={12} md={8}>
              <ButtonSp
                className={clsx(
                  classes.button,
                  classes.buttonMargin,
                  classes.width100
                )}
                onClick={_handleSaveSetting}
                fullWidth
              >
                {t('save-setting')}
              </ButtonSp>
            </Grid>
            {isDesktop && <Grid item xs={2}></Grid>}
          </Grid>
        )}
        {isSettingOpen && (
          <IconButton className={classes.closeButton} onClick={closeSetting}>
            <CloseIcon />
          </IconButton>
        )}
      </Paper>
    </Container>
  )
}

export default PdpaNewBanner
